import React, { useEffect, useState } from 'react';
import { Form, Input, Space, Popover, Button, FormInstance } from 'antd';
import InlineMarkdown from '../InlineMarkdown';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Param } from '../../cliDataSchema';

const OSEP_VERSION_REGEX = /^\d+\.\d+\.\d+$/;
const BUMP_MY_VERSION_DOCS_URL =
  'https://callowayproject.github.io/bump-my-version/explanation/version-parts/';

interface RemoteRefInputProps {
  value: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  param: Param;
  form: FormInstance;
}

const RemoteProjectVersion: React.FC<RemoteRefInputProps> = ({
  value,
  handleChange,
  param,
  form: _form,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [hasBlurred, setHasBlurred] = useState(false);
  const hasValidOsepVersion =
    !inputValue || OSEP_VERSION_REGEX.test(inputValue);
  const showVersionWarning = hasBlurred && !hasValidOsepVersion;
  const warningStatus = showVersionWarning ? 'warning' : undefined;
  const warningMessage = showVersionWarning ? (
    <span style={{ color: '#8c8c8c' }}>
      The provided version number does not meet OSEP standards (x.y.z). You can
      proceed and the generator will apply your version number to the project,
      but the resulting{' '}
      <span style={{ color: '#ff0004' }}>pipeline might fail</span> without
      custom adjustments to the project configuration.{' '}
      <a href={BUMP_MY_VERSION_DOCS_URL} target="_blank" rel="noreferrer">
        See more information
      </a>
    </span>
  ) : undefined;

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    handleChange(event);
  };

  const handleBlur = () => {
    setHasBlurred(true);
  };

  return (
    <Form.Item
      name={param.name}
      label={
        <Space size={0}>
          {param.osep_generator_ui_display_name}
          {param.help && (
            <Popover
              content={<InlineMarkdown>{param.help}</InlineMarkdown>}
              title={param.osep_generator_ui_display_name}
              trigger="click"
            >
              <Button
                size="middle"
                icon={<QuestionCircleOutlined />}
                type="text"
              />
            </Popover>
          )}
        </Space>
      }
      validateStatus={warningStatus}
      help={warningMessage}
      rules={
        param.required
          ? [
              {
                required: true,
                message: 'This field is required. ',
              },
            ]
          : undefined
      }
    >
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
      />
    </Form.Item>
  );
};

export default RemoteProjectVersion;
